import { validateAdvisorPlannerSearch } from '@itineraries/features/advisor-planner';
import { validateItinerariesSearch } from '@itineraries/features/itineraries-filters';
import { validatePaymentLogsSearch } from '@itineraries/features/payment-logs-filters';
import { validateItineraryDetailSearch } from '@itineraries/widgets/itinerary-view-tabs';
import { createRoute, lazyRouteComponent } from '@tanstack/react-router';
import { protectedRoute } from './_root';

export const advisorPlannerRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: 'advisor-planner',
  component: lazyRouteComponent(() =>
    import('@itineraries/pages/advisor-planner').then((m) => ({
      default: m.AdvisorPlannerPage,
    }))
  ),
  validateSearch: validateAdvisorPlannerSearch,
});

export const itinerariesRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: 'itineraries',
  component: lazyRouteComponent(() =>
    import('@itineraries/pages/itineraries-list').then((m) => ({
      default: m.ItinerariesListPage,
    }))
  ),
  validateSearch: validateItinerariesSearch,
});

// Literal sibling registered alongside the dynamic detail route below.
// TanStack Router prioritises literal segments over params on the same
// depth, so `/itineraries/create` resolves to the stub page even though
// `$itineraryId` would match anything. Stub is a placeholder until the
// full create-itinerary flow PRD lands.
export const itinerariesCreateRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: 'itineraries/create',
  component: lazyRouteComponent(() =>
    import('@itineraries/pages/itineraries-create').then((m) => ({
      default: m.ItinerariesCreatePage,
    }))
  ),
});

export const itineraryDetailRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: 'itineraries/$itineraryId',
  component: lazyRouteComponent(() =>
    import('@itineraries/pages/itinerary-detail').then((m) => ({
      default: m.ItineraryDetailPage,
    }))
  ),
  validateSearch: validateItineraryDetailSearch,
});

// ─── Itinerary Settings (inline validateSearch for the Information /
// Photos / Videos / CMS Title tabs; kept here so the page stays lazily code-split).
// Uses `?pane=` (not `?tab=`) to avoid colliding with the operations
// route's `tab` param in TanStack Router's cross-route search union. ──
const SETTINGS_TAB_IDS = [
  'information',
  'photos',
  'videos',
  'cms-title',
] as const;
type SettingsTabId = (typeof SETTINGS_TAB_IDS)[number];
type SettingsSearch = { pane?: SettingsTabId };

const isSettingsTabId = (value: unknown): value is SettingsTabId =>
  typeof value === 'string' &&
  (SETTINGS_TAB_IDS as readonly string[]).includes(value);

const validateSettingsSearch = (
  input: Record<string, unknown>
): SettingsSearch => {
  if (isSettingsTabId(input.pane)) return { pane: input.pane };
  return {};
};

export const itinerarySettingsRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: 'itineraries/$itineraryId/settings',
  component: lazyRouteComponent(() =>
    import('@itineraries/pages/itinerary-settings').then((m) => ({
      default: m.ItinerarySettingsPage,
    }))
  ),
  validateSearch: validateSettingsSearch,
});

export const itineraryPricesRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: 'itineraries/$itineraryId/prices',
  component: lazyRouteComponent(() =>
    import('@itineraries/pages/itinerary-prices').then((m) => ({
      default: m.ItineraryPricesPage,
    }))
  ),
});

// ─── Itinerary Item Edit (inline validateSearch) ──────────────────

type ItineraryItemEditSearch = {
  // Named `pane` (not `tab`) to avoid colliding with the operations-page
  // and itinerary-detail-page search params that also use `tab` with
  // different value spaces — useSearch({ strict: false }) unions them.
  pane?:
    | 'description'
    | 'images-and-videos'
    | 'attachments'
    | 'location-and-time'
    | 'pricing'
    | 'supplier'
    | 'facilities'
    | 'private-notes'
    | 'public-notes'
    | 'driver-notes'
    | 'operational-notes';
};

const validateItineraryItemEditSearch = (
  input: Record<string, unknown>
): ItineraryItemEditSearch => {
  const pane = input.pane;
  if (
    pane === 'description' ||
    pane === 'images-and-videos' ||
    pane === 'attachments' ||
    pane === 'location-and-time' ||
    pane === 'pricing' ||
    pane === 'supplier' ||
    pane === 'facilities' ||
    pane === 'private-notes' ||
    pane === 'public-notes' ||
    pane === 'driver-notes' ||
    pane === 'operational-notes'
  ) {
    return { pane };
  }
  return {};
};

export const itineraryItemEditRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: 'itineraries/$itineraryId/item/$itemId',
  component: lazyRouteComponent(() =>
    import('@itineraries/pages/itinerary-item-edit').then((m) => ({
      default: m.ItineraryItemEditPage,
    }))
  ),
  validateSearch: validateItineraryItemEditSearch,
});

export const managementPaymentLogsRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: 'management/payment-logs',
  component: lazyRouteComponent(() =>
    import('@itineraries/pages/payment-logs').then((m) => ({
      default: m.PaymentLogsPage,
    }))
  ),
  validateSearch: validatePaymentLogsSearch,
});

// Admin tool: criteria-scoped bulk itinerary status change. The page itself
// re-checks the admin role (the route tree only enforces authentication).
export const managementBulkStatusChangeRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: 'management/bulk-status-change',
  component: lazyRouteComponent(() =>
    import('@itineraries/pages/bulk-status-change').then((m) => ({
      default: m.BulkStatusChangePage,
    }))
  ),
});

export const managementEmbedGeneratorRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: 'management/embed-generator',
  component: lazyRouteComponent(() =>
    import('@auth/pages/embed-generator').then((m) => ({
      default: m.EmbedGeneratorPage,
    }))
  ),
  // Reuses the itineraries search shape — the embedded ItinerariesTable
  // reads filters/pagination/sort from this route's URL search.
  validateSearch: validateItinerariesSearch,
});

// User-facing Payments page (sidebar leaf, every consultant). Same search
// validator as the admin page; the page pins the list to the signed-in
// account.
export const paymentsRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: 'payments',
  component: lazyRouteComponent(() =>
    import('@itineraries/pages/payments').then((m) => ({
      default: m.PaymentsPage,
    }))
  ),
  validateSearch: validatePaymentLogsSearch,
});
